<template>
  <div class="profile-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="profile-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="profile-dropdown__anchor min-w-max">
            <slot />
            <div class="avatar-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="10" fill="url(#paint0_linear_5417_633)" />
                <text
                  x="10"
                  y="9"
                  fill="#ffffff"
                  font-size="12"
                  font-weight="400"
                  text-anchor="middle"
                  dominant-baseline="central"
                >
                  {{ userInitial }}
                </text>
                <defs>
                  <linearGradient
                    id="paint0_linear_5417_633"
                    x1="1.7196e-08"
                    y1="-4.44444"
                    x2="28.5556"
                    y2="25.2222"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop stop-color="#1FB2DE" />
                    <stop offset="0.4193" stop-color="#13B897" />
                    <stop offset="1" stop-color="#EAF123" />
                  </linearGradient>
                </defs>
              </svg>
            </div>
          </span>
        </VaButton>
      </template>
      <VaDropdownContent
        class="profile-dropdown__content md:w-60 px-0 py-4 w-full"
        :style="{ '--hover-color': hoverColor }"
      >
        <VaList v-for="group in effectiveOptions" :key="group.name">
          <header v-if="group.name" class="uppercase text-[var(--va-secondary)] opacity-80 font-bold text-xs px-4">
            {{ group.name }}
          </header>
          <VaListItem
            v-for="item in group.list"
            :key="item.name"
            class="menu-item px-4 text-base cursor-pointer h-8"
            v-bind="item.name !== 'Sign Out' ? resolveLinkAttribute(item) : {}"
            @click="item.name === 'Sign Out' ? handleLogout() : undefined"
          >
            <VaIcon :name="item.icon" class="pr-1" color="secondary" />
            {{ item.name }}
          </VaListItem>
          <VaListSeparator v-if="group.separator" class="mx-3 my-2" />
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { useColors } from 'vuestic-ui'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../../../../stores/auth'
import { useUserStore } from '../../../../stores/user-store'

const router = useRouter()
const authStore = useAuthStore()
const userStore = useUserStore()

const userInitial = computed(() => userStore.firstName.charAt(0).toUpperCase() || '?')

const handleLogout = () => {
  authStore.logout()
  router.push({ name: 'login' })
}

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

type ProfileListItem = {
  name: string
  to?: string
  query?: Record<string, unknown>
  href?: string
  icon: string
}

type ProfileOptions = {
  name: string
  separator: boolean
  list: ProfileListItem[]
}

const props = defineProps<{
  options?: ProfileOptions[]
}>()

const defaultOptions = computed<ProfileOptions[]>(() => [
  {
    name: '',
    separator: true,
    list: [
      {
        name: 'Profile',
        to: 'users',
        query: { userId: userStore.id },
        icon: 'mso-account_circle',
      },
    ],
  },
  {
    name: '',
    separator: false,
    list: [
      {
        name: 'Sign Out',
        to: '',
        icon: 'mso-logout',
      },
    ],
  },
])

const effectiveOptions = computed(() => props.options ?? defaultOptions.value)

const isShown = ref(false)

const resolveLinkAttribute = (item: ProfileListItem) => {
  return item.to ? { to: { name: item.to, query: item.query } } : item.href ? { href: item.href, target: '_blank' } : {}
}
</script>

<style lang="scss">
.profile-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-block;
  }
}
</style>
